/**
 * Central program content, sourced from the brochure
 * "M.S. Data Science (IITC x MU) - 11 Feb, 2026" (text-extracted + verified
 * against a page-by-page visual check) plus direct confirmation from edept
 * on specific figures. Every field that is still unconfirmed is marked TODO
 * — do not remove a TODO without a verified source.
 *
 * OPEN ITEM: the brochure's own cover banner states "6 months in India",
 * with no total program length stated anywhere in the document. The
 * original project brief said "18 months total". This is unresolved --
 * copy below uses the confirmed "6 months in India" phase language and
 * avoids asserting a specific total until edept confirms it.
 */

export const program = {
  name: 'Master of Applied Science in Data Science',
  shortName: 'M.A.S in Data Science',
  tagline: 'A Dual-Degree Global Programme',
  banner: 'Save Cost | Earn a U.S. STEM Degree', // brochure banner minus the unconfirmed total-duration claim
  partners: {
    deliveryPartner: 'edept',
    indiaUniversity: 'Mahindra University',
    usUniversity: 'Illinois Tech ',
  },
  // TODO: confirm total program length with edept (brochure states only the
  // India-phase length below; no total is printed in the source document).
  durationMonths: null as number | null,
  structure: [
    {
      phase: 'Study Phase - India',
      duration: '6 months',
      location: 'Mahindra University (India)',
      description:
        'Foundational and applied Data Science coursework at Mahindra University, with continuous academic monitoring and mentoring.',
    },
    {
      phase: 'Study Phase - U.S.',
      // TODO: confirm exact duration of the US phase once total program length is confirmed.
      duration: '12 Months',
      location: 'Illinois Tech, Chicago',
      description:
        'Advanced M.A.S in Data Science coursework at IIT Chicago, including capstone and project-based learning, leading to a STEM-designated MS in Data Science.',
    },
  ],
  admissionJourney: [
    {
      step: 1,
      title: 'Application Submission',
      description:
        'Apply through Illinois Tech with edept support by submitting academic, professional, and English-proficiency credentials.',
    },
    {
      step: 2,
      title: 'Academic Review and Eligibility',
      description:
        "Joint academic evaluation ensures alignment with Illinois Techʼs M.A.S. Data Science prerequisites; bridging courses assigned if required. ",
    },
    {
      step:3, 
      title: 'Interview by Mahindra University',
      description:
      "Shortlisted applicants complete an interview with Mahindra University, coordinated with edept support, as part of the academic evaluation.",
    },
    {
      step: 4,
      title: 'Conditional Offer',
      description:
        'Eligible students receive a conditional offer confirming India-phase admission and U.S. transfer requirements.',
    },
    {
      step: 5,
      title: 'U.S. Transition Preparation',
      description:
        'edept supports visa guidance, financial documentation, and pre-departure orientation for the U.S. phase.',
    },
    {
      step: 6,
      title: 'Study Phase—India',
      description:
        'Complete foundational and applied coursework at Mahindra University with continuous academic monitoring and mentoring.',
    },
    {
      step: 7,
      title: 'Study Phase—U.S.',
      description:
        'Begin advanced M.A.S. in Data Science coursework at Illinois Tech, including capstone and project-based learning.',
    },
    {
      step: 8,
      title: 'Graduation',
      description:
        'Graduate with a STEM-designated M.A.S. in Data Science from Illinois Tech.',
    },
  ],
  curriculum: {
    indiaTrack: {
      institution: 'Mahindra University',
      subtitle: 'Foundational and Applied Learning',
      modules: [
        'Programming for Data Science (Python)',
        'Statistics and Probability for Analytics',
        'Data Management and SQL',
        'Data Visualisation and Business Intelligence',
        'Introduction to Machine Learning',
        'Cloud and Data Infrastructure Fundamentals',
        'Applied Projects and Assessments',
        'PGP certification at the end'
      ],
    },
    usaTrack: {
      institution: 'Illinois Tech',
      subtitle: 'Master of Science in Data Science (STEM)',
      modules: [
        'Advanced Machine Learning',
        'Artificial Intelligence Foundations',
        'Big Data Analytics',
        'Data Engineering and High-Performance Computing',
        'Data Strategy and Governance',
        'Capstone Project/Master\u2019s Thesis',
        'Industry-Oriented Project Work',
        'Masters certification at the end'
      ],
    },
  },
  usps: [
    {
      title: 'Best value, without the price tag of a full overseas degree',
       icon: 'piggy-bank',
    },
    {
      title: 'Placement support in India and the U.S.',
    icon: 'briefcase',
    },
    {
      title: 'No GRE required',
      icon: 'check-circle',
    },
    {
      title: 'STEM OPT: up to 36 months',
      icon: 'languages',
    },
    {
      title: 'Guided visa and transition process',
      icon: 'plane',
    },
  ],
  admissions: {
    eligibility: [
      'A 4-year Bachelor\u2019s degree in engineering, computer science, mathematics, statistics, or a related field',
      '3 year degree eligible from NAAC A-rated institutions. The UG degree must be conferred in First Class with Distinction/Division I',
      'Strong quantitative background',
      'Programming exposure (Python/C++/Java) preferred, not required—free training is provided in these tools and for IELTS preparation',
    ],
    intakes: ['March 2026'],
    applicationUrl: import.meta.env.VITE_APPLY_URL || '#apply',
  },
  // Brochure page 11 stat callouts (Placements/Companies/Publications /
  // Awards). Read directly off the brochure page; edept to confirm exact
  // figures/definitions before publishing if these get updated.
  stats: [
    { value: '1500+', label: 'Placements' },
    { value: '400+', label: 'Companies' },
    { value: '1000+', label: 'Publications' },
    { value: '80+', label: 'Awards' },
  ],
rankings: {
  items: [
    {
      value: "#1",
      label: "In Illinois for the best salaries of graduates",
      source: "Wall Street Journal/College Pulse",
      year: "2026",
    },
    {
      value: "#22",
      label: "In the U.S. for the best salaries of graduates",
      source: "U.S. News and World Report",
      year: "2026",
    },
    {
      value: "Top 100",
      label: "World's Top Universities",
      source: "Time Magazine/Statista",
      year: "2026",
    },
    {
      value: "1 of 5",
      label:
        'Recognized as an Opportunity College and University*',
      source: "Carnegie Foundation/American Council on Education",
      year: "2025",
    },
  ],
},
  careers: {
    topRoles: [
      'Data Scientist',
      'Senior Data Analyst',
      'Machine Learning Engineer',
      'AI Engineer',
      'Business Analytics Manager',
      'Data Engineer',
      'Product and Growth Analyst',
      'Digital Transformation Analyst',
    ],
    hiringIndustries: [
      'Technology and Software',
      'Consulting',
      'Finance and FinTech',
      'Healthcare and Life Sciences',
      'Manufacturing and Industrial Analytics',
      'Startups and Innovation Labs',
    ],
  },
  faqs: [
    {
      q: 'What credentials will I receive?',
      a: 'A PGP certificate from Mahindra University and a Master of Applied Science in Data Science (STEM-designated) from Illinois Tech.',
    },
    {
      q: 'Does this program offer U.S. work opportunities?',
      a: 'Yes. STEM graduates are eligible for up to 36 months of OPT.',
    },
    {
      q: 'Is this better than a full M.S. abroad?',
      a: 'Yes, You will save on cost, have a smooth academic transition, and still earn a full U.S. degree.',
    },
    {
      q: ' Is there any placement support available?',
      a: 'Yes, we provide career support and employer exposure, though placements are not guaranteed.',
    },
    {
      q: 'Can I work while I study at Illinois Tech?',
      a: 'International students in valid F-1 status can generally work up to 20 hours/week on campus while classes are in session, and up to 40 hours/week when classes are not.',
    },
    {
      q: 'What if my visa is not approved?',
      a: 'Alternative academic pathways will be guided as per university and edept policies.',
    },
  ],
} as const;

export type ProgramData = typeof program;
